/**
 * Converts HitCompactViewProps (display-only, no interactive) to SrpJobCardViewModel
 * for JSON serialization in the embedded script.
 * Also provides srpJobCardToHitProps for rendering from view model.
 */
import type React from 'react';
import type { HitCompactViewProps } from '../../components/Hit/HitCompactViewProps';
import type { SrpJobCardViewModel, SrpPrioritizedLocationData } from './SrpViewModel';

function toPlainLeftBorderStyle(style: Record<string, unknown>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (style && typeof style === 'object') {
    Object.entries(style).forEach(([k, v]) => {
      if (v !== undefined && v !== null && typeof v !== 'function' && typeof v !== 'object') {
        out[k] = v as string | number;
      }
    });
  }
  return out;
}

function toSrpPrioritizedLocation(data: { display: string; locationForUrl: string; additionalCount: number; fullList: string }): SrpPrioritizedLocationData {
  return {
    display: data.display,
    locationForUrl: data.locationForUrl,
    additionalCount: data.additionalCount,
    fullList: data.fullList,
  };
}

/** Serialize date-like value for JSON (number or ISO string). */
function serializeDate(v: string | number | Date | null | undefined): number | string | null | undefined {
  if (v == null) return v;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') return v;
  if (v instanceof Date) return v.getTime();
  return undefined;
}

/**
 * Converts presentational hit props to the serializable view model shape.
 * Call only with props that have no interactive field (static shell).
 */
export function hitPropsToSrpJobCard(props: HitCompactViewProps): SrpJobCardViewModel {
  return {
    articleId: props.articleId,
    titleId: props.titleId,
    jobTitle: props.jobTitle,
    jobUrl: props.jobUrl,
    companyId: props.companyId,
    companyProfileUrl: props.companyProfileUrl,
    companyNameDisplay: props.companyNameDisplay,
    registryCompanyFormDescription: props.registryCompanyFormDescription,
    isTyomarkkinatori: props.isTyomarkkinatori,
    index: props.index,
    leftBorderStyle: toPlainLeftBorderStyle(props.leftBorderStyle as Record<string, unknown>),
    brandColour: props.brandColour,
    useBrandColour: props.useBrandColour,
    salaryLabel: props.salaryLabel,
    salaryInferenceExplanationKind: props.salaryInferenceExplanationKind ?? null,
    lastSeenLabel: props.lastSeenLabel,
    rawTitleSubtitle: props.rawTitleSubtitle,
    locale: props.locale,
    singleLocation: toSrpPrioritizedLocation(props.singleLocation),
    remoteness: props.remoteness,
    jobType: props.jobType,
    normalisedRoleLevel: props.normalisedRoleLevel,
    requiredLanguages: [...(props.requiredLanguages || [])],
    matchScore: props.matchScore,
    postedDate: serializeDate(props.postedDate),
    applicationDeadline: serializeDate(props.applicationDeadline),
    isSaved: props.isSaved ?? false,
    isApplied: props.isApplied ?? false,
    isViewed: props.isViewed ?? false,
    currentApplicationState: props.currentApplicationState ?? null,
  };
}

/**
 * Converts a view model job card back to HitCompactViewProps for rendering.
 * Used by SrpView to render the same HitCompactView with embedded view model data.
 */
export function srpJobCardToHitProps(card: SrpJobCardViewModel): HitCompactViewProps {
  const leftBorderStyle: React.CSSProperties = (card.leftBorderStyle || {}) as React.CSSProperties;
  return {
    articleId: card.articleId,
    titleId: card.titleId,
    jobTitle: card.jobTitle,
    jobUrl: card.jobUrl,
    companyId: card.companyId,
    companyProfileUrl: card.companyProfileUrl,
    companyNameDisplay: card.companyNameDisplay,
    registryCompanyFormDescription: card.registryCompanyFormDescription,
    isTyomarkkinatori: card.isTyomarkkinatori,
    index: card.index,
    leftBorderStyle,
    brandColour: card.brandColour,
    useBrandColour: card.useBrandColour,
    salaryLabel: card.salaryLabel,
    salaryInferenceExplanationKind: card.salaryInferenceExplanationKind ?? null,
    lastSeenLabel: card.lastSeenLabel,
    rawTitleSubtitle: card.rawTitleSubtitle,
    locale: card.locale,
    singleLocation: card.singleLocation as HitCompactViewProps['singleLocation'],
    remoteness: card.remoteness,
    jobType: card.jobType,
    normalisedRoleLevel: card.normalisedRoleLevel,
    requiredLanguages: Array.isArray(card.requiredLanguages) ? card.requiredLanguages : [],
    matchScore: card.matchScore,
    matchScoreData: null,
    postedDate: card.postedDate,
    applicationDeadline: card.applicationDeadline,
    isSaved: card.isSaved,
    isApplied: card.isApplied,
    isViewed: card.isViewed,
    currentApplicationState: card.currentApplicationState ?? null,
    // no interactive – read-only
  };
}
